// app/views/pages/Calendar/Show.tsx
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { useBasePath } from '@/lib/paths'
import { Calendar, Check, AlertCircle, Plus } from 'lucide-react'

interface CalendarEvent {
  id: string
  title: string
  due_date: string
  description: string | null
  source: 'system' | 'user' | 'admin'
  completed: boolean
  scs_type: string
  scs_id: string
  scs_label: string
  calendar_id: string
  completion_path: string | null
  event_path: string | null
}

type Props = {
  events: CalendarEvent[]
  filters: { scs_type: string | null }
  create_event_path: string
}

const SCS_DOT_COLORS: Record<string, string> = {
  formation: 'bg-blue-500',
  tax_registration: 'bg-purple-500',
  compliance_filing: 'bg-orange-500',
  nominee_shareholder: 'bg-emerald-500',
  nominee_director: 'bg-teal-500',
  business_task: 'bg-rose-500',
  registered_agent: 'bg-cyan-500',
  registered_address: 'bg-yellow-500',
  license: 'bg-lime-500',
  standalone: 'bg-indigo-500',
}

const SCS_LABELS: Record<string, string> = {
  formation: 'Formation',
  tax_registration: 'Tax Registration',
  compliance_filing: 'Compliance Filing',
  nominee_shareholder: 'Nominee Shareholder',
  nominee_director: 'Nominee Director',
  business_task: 'Business Task',
  registered_agent: 'Registered Agent',
  registered_address: 'Registered Address',
  license: 'License',
  standalone: 'Custom',
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function relativeDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diff < 0) return `${Math.abs(diff)}d overdue`
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  return `in ${diff}d`
}

function groupEvents(events: CalendarEvent[]) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const endOfWeek = new Date(today)
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()))

  const endOfNextWeek = new Date(endOfWeek)
  endOfNextWeek.setDate(endOfWeek.getDate() + 7)

  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

  const groups: Record<string, CalendarEvent[]> = {
    overdue: [],
    this_week: [],
    next_week: [],
    this_month: [],
    later: [],
  }

  events.forEach(event => {
    const date = new Date(event.due_date + 'T00:00:00')
    if (date < today) groups.overdue.push(event)
    else if (date <= endOfWeek) groups.this_week.push(event)
    else if (date <= endOfNextWeek) groups.next_week.push(event)
    else if (date <= endOfMonth) groups.this_month.push(event)
    else groups.later.push(event)
  })

  return groups
}

function EventRow({ event }: { event: CalendarEvent }) {
  const isOverdue = new Date(event.due_date + 'T00:00:00') < new Date(new Date().toDateString())

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t first:border-t-0">
      <div className="flex items-start gap-3 min-w-0">
        <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${SCS_DOT_COLORS[event.scs_type] ?? 'bg-gray-400'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{event.title}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
            <span>{event.scs_label}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        <div className="text-right">
          <div className="text-sm text-foreground">{formatDate(event.due_date)}</div>
          <div className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
            {isOverdue && <AlertCircle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
            {relativeDate(event.due_date)}
          </div>
        </div>
        {event.completion_path && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => router.post(event.completion_path!)}
          >
            <Check className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

function AddEventDialog({
  createEventPath,
  onClose,
}: {
  createEventPath: string
  onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const todayStr = new Date().toISOString().split('T')[0]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title || !dueDate) return
    setSubmitting(true)
    router.post(
      createEventPath,
      { event: { title, due_date: dueDate, description: description || undefined } },
      { onFinish: () => { setSubmitting(false); onClose() } }
    )
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="cal-title">Event Title</Label>
            <Input
              id="cal-title"
              placeholder="e.g. Contract expiry, Annual return deadline"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cal-due-date">Due Date</Label>
            <Input
              id="cal-due-date"
              type="date"
              min={todayStr}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cal-description">Description (optional)</Label>
            <Input
              id="cal-description"
              placeholder="Add a note..."
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={!title || !dueDate || submitting}
            >
              {submitting ? 'Saving...' : 'Add Event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function CalendarShow({ events, filters, create_event_path }: Props) {
  const basePath = useBasePath()
  const [addOpen, setAddOpen] = useState(false)
  const groups = groupEvents(events)

  const groupLabels: Record<string, string> = {
    overdue: 'Overdue',
    this_week: 'This Week',
    next_week: 'Next Week',
    this_month: 'This Month',
    later: 'Later',
  }

  return (
    <DashboardLayout title="Calendar" breadcrumbs={[{ label: 'Calendar' }]}>
      {addOpen && (
        <AddEventDialog createEventPath={create_event_path} onClose={() => setAddOpen(false)} />
      )}

      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground">Upcoming events and deadlines across all services</p>
          <div className="flex items-center gap-2 mt-3">
            <Select
              value={filters.scs_type ?? 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  router.get(`${basePath}/calendar`)
                } else {
                  router.get(`${basePath}/calendar`, { scs_type: value })
                }
              }}
            >
              <SelectTrigger className="w-45 h-9 text-sm">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {Object.entries(SCS_LABELS).map(([type, label]) => (
                  <SelectItem key={type} value={type}>
                    <span className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${SCS_DOT_COLORS[type] ?? 'bg-gray-400'}`} />
                      {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Add Event
            </Button>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No upcoming events</h3>
            <p className="text-sm text-muted-foreground mb-4">Add custom events or they'll appear here as you use services</p>
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Add your first event
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupLabels).map(([key, label]) => {
              const groupEventsArr = groups[key]
              if (!groupEventsArr || groupEventsArr.length === 0) return null
              return (
                <div key={key}>
                  <h2 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${key === 'overdue' ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {label} ({groupEventsArr.length})
                  </h2>
                  <div className="border rounded-lg divide-y">
                    {groupEventsArr.map(event => (
                      <EventRow key={`${event.scs_type}-${event.id}`} event={event} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
